import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { NativeSelect } from '@/components/form';
import { MenuLink } from '@/components/menu-link';
import { EmptyState, PageHeader } from '@/components/page-header';
import { ExpensesMoneyBar } from '@/components/purchases/purchase-money-bar';
import { PurchaseTable } from '@/components/purchases/purchase-table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useMe } from '@/lib/clients';
import { PERIODS, periodStart } from '@/lib/dates';
import { useOrgId } from '@/lib/org';
import { usePurchaseTransactions, type PurchaseRow } from '@/lib/purchases';

export const Route = createFileRoute('/expenses/')({ component: Expenses });

const TYPES: Array<[string, string]> = [
  ['all', 'All transactions'],
  ['bill', 'Bills'],
  ['expense', 'Expenses'],
  ['vendor_payment', 'Bill payments'],
  ['debit_note', 'Debit notes'],
];
/** Payee filter value for expenses without a payee. */
const NO_PAYEE = 'none';
const open = (r: PurchaseRow) => ['approved', 'partially_paid'].includes(r.status);
const STATUSES: Array<[string, string, (r: PurchaseRow) => boolean]> = [
  ['all', 'All statuses', () => true],
  ['open', 'Open', open],
  ['overdue', 'Overdue', (r) => r.overdue],
  ['draft', 'Draft', (r) => r.status === 'draft'],
  ['paid', 'Paid / closed', (r) => ['paid', 'posted'].includes(r.status)],
  ['void', 'Void', (r) => r.status === 'void'],
];
const BAR: Record<string, (r: PurchaseRow) => boolean> = {
  spend: (r) =>
    ['bill', 'expense', 'debit_note'].includes(r.type) && !['draft', 'void'].includes(r.status),
  overdue: (r) => r.type === 'bill' && open(r) && r.overdue,
  not_due: (r) => r.type === 'bill' && open(r) && !r.overdue,
  draft: (r) => r.status === 'draft',
  credit: (r) =>
    (r.type === 'debit_note' && open(r)) ||
    (r.type === 'vendor_payment' && r.status === 'posted' && r.balance !== '0.00'),
};

/** Expenses (document 06 E1): bills, expenses, bill payments and debit notes with filters. */
function Expenses() {
  const orgId = useOrgId();
  const me = useMe();
  const tx = usePurchaseTransactions();
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [period, setPeriod] = useState('all');
  const [payee, setPayee] = useState('all');
  const [q, setQ] = useState('');
  const [bar, setBar] = useState<string | null>(null);
  const payees = useMemo(
    () =>
      [
        ...new Map(
          (tx.data ?? []).map((r) => [r.contactId ?? NO_PAYEE, r.contactName ?? 'No payee']),
        ).entries(),
      ].sort((a, b) => a[1].localeCompare(b[1])),
    [tx.data],
  );
  const since = periodStart(period);
  const needle = q.trim().toLowerCase();
  const statusFn = STATUSES.find((s) => s[0] === status)![2];
  const rows = (tx.data ?? []).filter(
    (r) =>
      (type === 'all' || r.type === type) &&
      statusFn(r) &&
      (!since || r.date >= since) &&
      (payee === 'all' || (r.contactId ?? NO_PAYEE) === payee) &&
      (!bar || BAR[bar]!(r)) &&
      (!needle ||
        (r.contactName ?? '').toLowerCase().includes(needle) ||
        (r.number ?? '').toLowerCase().includes(needle) ||
        (r.reference ?? '').toLowerCase().includes(needle) ||
        (r.category ?? '').toLowerCase().includes(needle) ||
        (r.memo ?? '').toLowerCase().includes(needle)),
  );
  return (
    <>
      <PageHeader
        title="Expenses"
        actions={
          me.data?.isStaff ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="gap-1">
                  New transaction <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <MenuLink to="/expenses/expenses/new">Expense</MenuLink>
                <MenuLink to="/expenses/bills/new">Bill</MenuLink>
                <MenuLink to="/expenses/pay-bills">Pay bills</MenuLink>
                <MenuLink to="/expenses/debit-notes/new">Debit note</MenuLink>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null
        }
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : (
        <>
          <ExpensesMoneyBar active={bar} onSelect={setBar} />
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <NativeSelect
              aria-label="Type"
              className="w-[180px] bg-card"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {TYPES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label="Status"
              className="w-[160px] bg-card"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUSES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label="Date"
              className="w-[160px] bg-card"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {PERIODS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label="Payee"
              className="w-[220px] bg-card"
              value={payee}
              onChange={(e) => setPayee(e.target.value)}
            >
              <option value="all">All payees</option>
              {payees.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </NativeSelect>
            <div className="relative w-[300px]">
              <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                className="bg-card pl-8"
                placeholder="Search by payee, no., category or memo"
                aria-label="Search expenses"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
          {tx.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : tx.isError ? (
            <EmptyState title="Could not load expenses" hint={tx.error.message} />
          ) : rows.length === 0 ? (
            <EmptyState
              title={tx.data.length ? 'No transactions match these filters' : 'No expenses yet'}
              hint="Bills, expenses, bill payments and debit notes appear here."
            />
          ) : (
            <PurchaseTable rows={rows} />
          )}
        </>
      )}
    </>
  );
}
